import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Tabs,
  Tab,
  Chip,
  Stack,
  TextField,
  Grid,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as JsonIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  AddPhotoAlternate as AddMoreIcon,
} from '@mui/icons-material';
import api from '../api/api';
import { AppIconButton } from './ui';

interface MenuItem {
  name: string;
  description: string;
  price: number;
  available: boolean;
  tags?: string[];
  portions?: Array<{
    name: string;
    price: number;
  }>;
}

interface MenuCategory {
  name: string;
  description: string;
  items: MenuItem[];
}

interface MenuData {
  menuName?: string;
  categories: MenuCategory[];
}

// Types matching backend response
interface FileProgress {
  fileName: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  totalPages: number;
  processedPages: number;
  extractedItems: number;
  errorMessage?: string;
}

interface ExtractionProgress {
  success: boolean;
  message: string;
  status: 'PENDING' | 'EXTRACTING_TEXT' | 'STRUCTURING_MENU' | 'VALIDATING' | 'COMPLETED' | 'FAILED';
  totalFiles: number;
  processedFiles: number;
  extractedCategories: number;
  extractedItems: number;
  fileProgressList: FileProgress[];
  menuDataPreview?: {
    menuName: string;
    categories: Array<{
      name: string;
      description: string;
      itemCount: number;
      items: MenuItem[];
    }>;
    extractedJson: string;
  };
  errorDetails?: string;
}

interface MenuFileUploadProps {
  onMenuDataExtracted?: (data: MenuData) => void;
  onJsonFileSelected?: (file: File) => void;
  onExtractionStart?: () => void;
  onExtractionComplete?: (success: boolean, categories: number, items: number) => void;
  disabled?: boolean;
}

const MenuFileUpload: React.FC<MenuFileUploadProps> = ({
  onMenuDataExtracted,
  onJsonFileSelected,
  onExtractionStart,
  onExtractionComplete,
  disabled = false,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extractedData, setExtractedData] = useState<MenuData | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [editingData, setEditingData] = useState<MenuData | null>(null);
  const [tabValue, setTabValue] = useState(1); // Default to Image/PDF Extract tab
  const [extractionProgress, setExtractionProgress] = useState<ExtractionProgress | null>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    setError(null);
    setExtractedData(null);
    setExtractionProgress(null);

    // Handle JSON file (single file only for JSON)
    if (fileArray.length === 1 && fileArray[0].type === 'application/json') {
      const file = fileArray[0];
      setSelectedFiles([file]);

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target?.result as string);
          const menuData: MenuData = {
            menuName: jsonData.menuName || jsonData.vendor?.businessName || 'Menu',
            categories: jsonData.categories || [],
          };
          setExtractedData(menuData);
          setEditingData(menuData);

          // Create a JSON file for the parent
          const jsonBlob = new Blob([JSON.stringify(menuData, null, 2)], { type: 'application/json' });
          const jsonFile = new File([jsonBlob], 'menu-data.json', { type: 'application/json' });

          if (onJsonFileSelected) {
            onJsonFileSelected(jsonFile);
          }
        } catch {
          setError('Invalid JSON file format');
        }
      };
      reader.readAsText(file);
      return;
    }

    // Handle image/PDF files (can be multiple)
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    for (const file of fileArray) {
      if (file.type.startsWith('image/') || file.type === 'application/pdf') {
        if (file.size > 10 * 1024 * 1024) {
          invalidFiles.push(`${file.name}: Exceeds 10MB limit`);
        } else {
          validFiles.push(file);
        }
      } else {
        invalidFiles.push(`${file.name}: Unsupported file type`);
      }
    }

    if (invalidFiles.length > 0) {
      setError(`Some files were skipped: ${invalidFiles.join(', ')}`);
    }

    if (validFiles.length === 0) {
      setError('No valid files to process. Please upload image or PDF files under 10MB.');
      return;
    }

    setSelectedFiles(validFiles);

    // Auto-start extraction for image/PDF files
    await handleExtractMenu(validFiles);
  };

  const handleAddMoreFiles = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newFiles = Array.from(files).filter(
      f => (f.type.startsWith('image/') || f.type === 'application/pdf') && f.size <= 10 * 1024 * 1024
    );

    if (newFiles.length === 0) {
      setError('No valid files to add. Please upload image or PDF files under 10MB.');
      return;
    }

    const allFiles = [...selectedFiles, ...newFiles];
    setSelectedFiles(allFiles);
    setError(null);

    // Re-run extraction with all files
    await handleExtractMenu(allFiles);
  };

  const handleExtractMenu = async (files: File[]) => {
    if (files.length === 0) return;

    console.log('Starting menu extraction for', files.length, 'files');
    setExtracting(true);
    setError(null);
    setExtractionProgress(null);

    if (onExtractionStart) {
      onExtractionStart();
    }

    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      console.log('Calling extraction API with', files.length, 'files...');
      const response = await api.post('/api/menu-extraction/extract-multiple', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('Extraction API response:', response.data);
      const result: ExtractionProgress = response.data;
      setExtractionProgress(result);

      if (result.success && result.menuDataPreview) {
        // Convert preview to MenuData format
        const menuData: MenuData = {
          menuName: result.menuDataPreview.menuName || 'Extracted Menu',
          categories: result.menuDataPreview.categories.map(cat => ({
            name: cat.name,
            description: cat.description || '',
            items: cat.items || [],
          })),
        };

        setExtractedData(menuData);
        setEditingData(menuData);

        // Create JSON file for the parent component
        const jsonBlob = new Blob([result.menuDataPreview.extractedJson], { type: 'application/json' });
        const jsonFile = new File([jsonBlob], 'extracted-menu.json', { type: 'application/json' });

        if (onJsonFileSelected) {
          onJsonFileSelected(jsonFile);
        }
        if (onMenuDataExtracted) {
          onMenuDataExtracted(menuData);
        }
        if (onExtractionComplete) {
          onExtractionComplete(true, result.extractedCategories, result.extractedItems);
        }
      } else {
        setError(result.message || 'Failed to extract menu');
        if (onExtractionComplete) {
          onExtractionComplete(false, 0, 0);
        }
      }
    } catch (err: unknown) {
      console.error('Extraction error:', err);
      const axiosError = err as { response?: { data?: ExtractionProgress & { message?: string; errorDetails?: string } } };
      const errorMessage = axiosError.response?.data?.message || axiosError.response?.data?.errorDetails || 'Failed to extract menu from files';
      setError(errorMessage);

      if (axiosError.response?.data) {
        setExtractionProgress(axiosError.response.data);
      }

      if (onExtractionComplete) {
        onExtractionComplete(false, 0, 0);
      }
    } finally {
      setExtracting(false);
    }
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);

    if (newFiles.length === 0) {
      setExtractedData(null);
      setExtractionProgress(null);
    }
  };

  const handleSaveExtracted = () => {
    if (editingData) {
      if (onMenuDataExtracted) {
        onMenuDataExtracted(editingData);
      }
      setShowPreview(false);

      // Convert to JSON file for the backend
      const jsonBlob = new Blob([JSON.stringify(editingData, null, 2)], {
        type: 'application/json',
      });
      const jsonFile = new File([jsonBlob], 'extracted-menu.json', {
        type: 'application/json',
      });

      if (onJsonFileSelected) {
        onJsonFileSelected(jsonFile);
      }
    }
  };

  const handleEditItem = (categoryIndex: number, itemIndex: number, field: keyof MenuItem, value: string | number | boolean) => {
    if (!editingData) return;

    const newData = { ...editingData };
    const item = newData.categories[categoryIndex].items[itemIndex];
    if (field === 'name' || field === 'description') {
      item[field] = value as string;
    } else if (field === 'price') {
      item[field] = value as number;
    } else if (field === 'available') {
      item[field] = value as boolean;
    }
    setEditingData(newData);
  };

  const getFileIcon = (file: File) => {
    if (file.type === 'application/json') return <JsonIcon />;
    if (file.type === 'application/pdf') return <PdfIcon />;
    if (file.type.startsWith('image/')) return <ImageIcon />;
    return <UploadIcon />;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED': return <CheckIcon sx={{ color: 'var(--status-ok)' }} />;
      case 'FAILED': return <ErrorIcon sx={{ color: 'var(--status-bad)' }} />;
      case 'PROCESSING': return <CircularProgress size={20} />;
      default: return <PendingIcon sx={{ color: 'var(--text-tertiary)' }} />;
    }
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(_, val) => setTabValue(val)}
          textColor="inherit"
          variant="fullWidth"
          sx={{
            mb: 2,
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTabs-flexContainer': {
              gap: { xs: 1, sm: 1.5 },
              display: 'flex',
              justifyContent: 'center'
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '10px',
              minHeight: { xs: 44, sm: 48 },
              padding: { xs: '10px 12px', sm: '12px 20px' },
              whiteSpace: 'normal',
              fontSize: { xs: '0.813rem', sm: '0.95rem' },
              lineHeight: { xs: '1.2', sm: '1.4' },
              flex: { xs: '0 1 48%', sm: '1 1 auto' },
              maxWidth: { xs: '48%', sm: 'none' },
              backgroundColor: 'rgba(96, 165, 250, 0.2) !important',
              color: 'var(--text-link) !important',
              border: '1px solid rgba(96, 165, 250, 0.31) !important',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(96, 165, 250, 0.25) !important',
                borderColor: 'rgba(96, 165, 250, 0.4) !important'
              }
            },
            '& .MuiTab-root.Mui-selected': {
              backgroundColor: 'rgba(96, 165, 250, 0.3) !important',
              border: '2px solid rgba(96, 165, 250, 0.5) !important',
              color: 'var(--text-link) !important',
              fontWeight: 700
            }
          }}
        >
          <Tab label="Upload JSON File" />
          <Tab label="Extract Image/PDF" />
        </Tabs>

        {tabValue === 0 ? (
          // JSON Upload Tab
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Upload a pre-formatted JSON file with menu data
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<JsonIcon />}
              disabled={disabled || extracting}
              fullWidth
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: 'rgba(96, 165, 250, 0.2)',
                color: 'var(--text-link)',
                border: '1px solid rgba(96, 165, 250, 0.31)',
                '&:hover': {
                  backgroundColor: 'rgba(96, 165, 250, 0.25)',
                  borderColor: 'rgba(96, 165, 250, 0.4)'
                }
              }}
            >
              Select Menu JSON File
              <input
                type="file"
                hidden
                accept=".json"
                onChange={handleFileSelect}
              />
            </Button>
          </Box>
        ) : (
          // Image/PDF Extraction Tab
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Upload menu images or PDFs - extraction runs automatically. You can select multiple files.
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
              disabled={disabled || extracting}
              fullWidth
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: 'rgba(96, 165, 250, 0.2)',
                color: 'var(--text-link)',
                border: '1px solid rgba(96, 165, 250, 0.31)',
                '&:hover': {
                  backgroundColor: 'rgba(96, 165, 250, 0.25)',
                  borderColor: 'rgba(96, 165, 250, 0.4)'
                }
              }}
            >
              Select Menu Images or PDFs (Multiple Allowed)
              <input
                type="file"
                hidden
                accept="image/*,.pdf"
                multiple
                onChange={handleFileSelect}
              />
            </Button>
          </Box>
        )}

        {/* Selected Files List */}
        {selectedFiles.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Selected Files ({selectedFiles.length})
            </Typography>
            <List dense sx={{ bgcolor: 'var(--bg-sunken)', borderRadius: 1, p: 0.5 }}>
              {selectedFiles.map((file, index) => {
                const fileProgress = extractionProgress?.fileProgressList?.find(
                  fp => fp.fileName === file.name
                );
                return (
                  <ListItem
                    key={`${file.name}-${index}`}
                    sx={{
                      bgcolor: 'white',
                      borderRadius: 1,
                      mb: 0.5,
                      border: '1px solid',
                      borderColor: fileProgress?.status === 'FAILED' ? '#fecaca' :
                        fileProgress?.status === 'COMPLETED' ? '#bbf7d0' : '#e2e8f0',
                    }}
                    secondaryAction={
                      !extracting && (
                        <AppIconButton
                          icon={<CloseIcon fontSize="small" />}
                          tooltip="Remove file"
                          variant="ghost"
                          size="small"
                          edge="end"
                          onClick={() => handleRemoveFile(index)}
                        />
                      )
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {fileProgress ? getStatusIcon(fileProgress.status) : getFileIcon(file)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {file.name}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {(file.size / 1024).toFixed(1)} KB
                            {fileProgress?.extractedItems !== undefined && fileProgress.extractedItems > 0 && (
                              <span style={{ color: 'var(--status-ok)', marginLeft: 8 }}>
                                {fileProgress.extractedItems} items extracted
                              </span>
                            )}
                          </Typography>
                          {fileProgress?.errorMessage && (
                            <Typography variant="caption" color="error" display="block">
                              {fileProgress.errorMessage}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>

            {/* Add More Files Button */}
            {!extracting && selectedFiles.length > 0 && tabValue === 1 && (
              <Button
                variant="text"
                component="label"
                startIcon={<AddMoreIcon />}
                size="small"
                sx={{ mt: 1, textTransform: 'none' }}
              >
                Add More Files
                <input
                  type="file"
                  hidden
                  accept="image/*,.pdf"
                  multiple
                  onChange={handleAddMoreFiles}
                />
              </Button>
            )}
          </Box>
        )}

        {/* Extraction Progress */}
        {extracting && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" icon={<CircularProgress size={20} />}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Extracting menu data from {selectedFiles.length} file(s)...
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This may take a moment depending on the file size and complexity.
              </Typography>
            </Alert>
            <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />
          </Box>
        )}

        {/* Extraction Results Summary */}
        {extractionProgress && !extracting && (
          <Box sx={{ mt: 2 }}>
            {extractionProgress.success ? (
              <Alert
                severity="success"
                icon={<CheckIcon />}
                action={
                  extractedData && (
                    <Button
                      color="inherit"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => setShowPreview(true)}
                    >
                      Preview/Edit
                    </Button>
                  )
                }
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {extractionProgress.message}
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  {extractionProgress.extractedCategories} categories, {extractionProgress.extractedItems} items ready for import
                </Typography>
              </Alert>
            ) : (
              <Alert severity="error" icon={<ErrorIcon />}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {extractionProgress.message}
                </Typography>
                {extractionProgress.errorDetails && (
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    {extractionProgress.errorDetails}
                  </Typography>
                )}
              </Alert>
            )}
          </Box>
        )}

        {/* JSON File Ready Message */}
        {tabValue === 0 && extractedData && !extracting && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="success" icon={<CheckIcon />}>
              Menu data ready - {extractedData.categories.length} categories with{' '}
              {extractedData.categories.reduce((sum, cat) => sum + cat.items.length, 0)} items.
              This will be automatically processed when you submit the form.
            </Alert>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setShowPreview(true)}
              sx={{ mt: 1 }}
            >
              Preview/Edit
            </Button>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Preview Dialog */}
      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="h6">Menu Preview</Typography>
            {extractedData && (
              <Chip
                label={`${extractedData.categories.length} Categories`}
                size="small"
                color="primary"
              />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent>
          {editingData && (
            <Box>
              {editingData.categories.map((category, catIndex) => (
                <Box key={catIndex} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {category.name}
                  </Typography>
                  {category.description && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {category.description}
                    </Typography>
                  )}
                  <Divider sx={{ my: 1 }} />

                  {category.items.map((item, itemIndex) => (
                    <Paper
                      key={itemIndex}
                      variant="outlined"
                      sx={{ p: 2, mb: 1 }}
                    >
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={5}>
                          <TextField
                            size="small"
                            fullWidth
                            value={item.name}
                            onChange={(e) =>
                              handleEditItem(catIndex, itemIndex, 'name', e.target.value)
                            }
                            label="Item Name"
                          />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <TextField
                            size="small"
                            fullWidth
                            value={item.description}
                            onChange={(e) =>
                              handleEditItem(catIndex, itemIndex, 'description', e.target.value)
                            }
                            label="Description"
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <TextField
                            size="small"
                            fullWidth
                            type="number"
                            value={item.price}
                            onChange={(e) =>
                              handleEditItem(catIndex, itemIndex, 'price', parseFloat(e.target.value) || 0)
                            }
                            label="Price"
                          />
                        </Grid>

                        {/* Show portions if available */}
                        {item.portions && item.portions.length > 0 && (
                          <Grid item xs={12}>
                            <Stack direction="row" spacing={1}>
                              <Typography variant="caption" color="text.secondary">
                                Portions:
                              </Typography>
                              {item.portions.map((portion, pIndex) => (
                                <Chip
                                  key={pIndex}
                                  label={`${portion.name}: ${portion.price}`}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </Stack>
                          </Grid>
                        )}

                        {/* Show tags if available */}
                        {item.tags && item.tags.length > 0 && (
                          <Grid item xs={12}>
                            <Stack direction="row" spacing={1}>
                              <Typography variant="caption" color="text.secondary">
                                Tags:
                              </Typography>
                              {item.tags.map((tag, tIndex) => (
                                <Chip
                                  key={tIndex}
                                  label={tag}
                                  size="small"
                                  color={tag === 'Vegetarian' ? 'success' : 'default'}
                                />
                              ))}
                            </Stack>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                  ))}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreview(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveExtracted}
            startIcon={<CheckIcon />}
          >
            Use This Menu
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MenuFileUpload;
